import React from "react";
import { Reveal, SectionOverline } from "./Shared";

const NervousSystem = () => {
  return (
    <section
      id="nervous-system"
      data-testid="nervous-system-section"
      className="relative py-32 sm:py-40 border-t forr-canvas"
      style={{ borderColor: "var(--forr-line-soft)" }}
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
        <div className="grid grid-cols-12 gap-8 items-center">
          <div className="col-span-12 lg:col-span-6 order-2 lg:order-1">
            <Reveal>
              <SectionOverline
                number="04"
                label="How The Nervous System Adapts"
                testId="nervous-system-overline"
              />
            </Reveal>
            <Reveal delay={0.05}>
              <h2
                data-testid="nervous-system-heading"
                className="mt-8 font-display forr-text-ink text-4xl sm:text-5xl md:text-6xl font-medium tracking-[-0.02em] leading-[1.05]"
              >
                Your physiology has already learned{" "}
                <span className="forr-text-mute">how to be you.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-8 max-w-xl text-lg font-light forr-text-soft leading-relaxed">
                Every repeated experience — stress, avoidance, dysregulation — writes
                itself into how your body responds. Over time, the response becomes
                automatic. It doesn’t need your permission.
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <div className="mt-12 space-y-6 max-w-xl">
                {[
                  {
                    step: "Input",
                    body: "A repeated experience — a stressor, a coping behavior, a physiological demand.",
                  },
                  {
                    step: "Adaptation",
                    body: "The nervous system encodes a faster, more efficient response to that input.",
                  },
                  {
                    step: "Automation",
                    body: "The response fires before conscious thought, becoming your default state.",
                  },
                ].map((s, i) => (
                  <div
                    key={s.step}
                    data-testid={`adaptation-step-${i + 1}`}
                    className="flex items-start gap-6"
                  >
                    <div
                      className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium"
                      style={{
                        backgroundColor: "var(--forr-teal-tint)",
                        color: "var(--forr-teal-deep)",
                        border: "1px solid var(--forr-teal-soft)",
                      }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <div>
                      <div className="font-display forr-text-ink text-lg font-medium">
                        {s.step}
                      </div>
                      <div className="mt-1 text-sm font-light forr-text-soft leading-relaxed">
                        {s.body}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          {/* Neural signal visualization */}
          <div className="col-span-12 lg:col-span-6 order-1 lg:order-2">
            <Reveal>
              <div
                data-testid="nervous-system-visual"
                className="relative aspect-square rounded-[24px] overflow-hidden forr-surface"
                style={{ border: "1px solid var(--forr-line)", boxShadow: "var(--forr-shadow-md)" }}
              >
                <div className="absolute inset-0 forr-radial-teal opacity-60" />
                <svg className="absolute inset-0 w-full h-full opacity-40" viewBox="0 0 400 400">
                  <defs>
                    <pattern id="grid-light" width="40" height="40" patternUnits="userSpaceOnUse">
                      <path
                        d="M 40 0 L 0 0 0 40"
                        fill="none"
                        stroke="rgba(10,31,38,0.06)"
                        strokeWidth="0.5"
                      />
                    </pattern>
                  </defs>
                  <rect width="400" height="400" fill="url(#grid-light)" />
                </svg>

                <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 400">
                  <defs>
                    <linearGradient id="signal-light" x1="0" x2="1">
                      <stop offset="0%" stopColor="#0FB5A6" stopOpacity="0" />
                      <stop offset="30%" stopColor="#0FB5A6" stopOpacity="0.95" />
                      <stop offset="70%" stopColor="#14C9B8" stopOpacity="0.95" />
                      <stop offset="100%" stopColor="#0FB5A6" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  <path
                    d="M0 220 L60 220 L80 220 L92 100 L104 320 L118 180 L140 220 L200 220 L220 220 L232 60 L244 340 L258 180 L280 220 L400 220"
                    fill="none"
                    stroke="url(#signal-light)"
                    strokeWidth="1.6"
                    className="forr-wave-flow"
                  />
                  <path
                    d="M0 260 L400 260"
                    stroke="rgba(10,31,38,0.12)"
                    strokeWidth="1"
                    strokeDasharray="4 6"
                  />
                </svg>

                <div className="absolute inset-[35%] rounded-full bg-teal-400/30 blur-2xl forr-breathe" />
                <div className="absolute inset-[43%] rounded-full forr-breathe" style={{ backgroundColor: "var(--forr-teal)" }} />

                <div className="absolute top-6 left-6 text-[10px] uppercase tracking-[0.28em] forr-text-mute">
                  Signal
                </div>
                <div className="absolute bottom-6 right-6 text-[10px] uppercase tracking-[0.28em] forr-text-teal-deep">
                  Adaptation → Automation
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NervousSystem;
